import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { IconButton, Typography } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { createElement, FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import EditModificationRequestItem from '../../../../entity/EditModificationRequestItem';
import UploadingFile from '../../../../entity/UploadingFile';
import {
  DeliverableForCraetionDetailFragment,
  ModificationRequestItemForDetailFragment,
} from '../../../../generated/graphql';
import hmsToSeconds from '../../../../utility/hmsToSeconds';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import AvatarAndName from '../AvatarAndName';
import MediaPlayer from '../MediaPlayer';
import _EditModificationRequestButton from './EditModificationRequestButton';
import InputCancelDialog from './InputCancelDialog';

interface Props {
  item: ModificationRequestItemForDetailFragment;
  deliverable: DeliverableForCraetionDetailFragment | undefined;
  editModificationRequestItem: EditModificationRequestItem;
  files: Array<FileReference>;
  bucketId?: string;
  index: number;
  disabled: boolean;
  onClickRemoveButton?: (modificationRequestItemId: string) => void;
  onRemoveEditAssetFile: (fileId: string) => void;
  onOpenEditModificationRequestDialog: (index: number) => void;
  onCloseEditModificationRequestDialog: () => void;
  onSubmitEditModificationRequestDialog: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;
  className?: string;
}

const Item: FC<Props> = ({
  item,
  deliverable,
  editModificationRequestItem,
  files,
  bucketId,
  index,
  disabled,
  onClickRemoveButton,
  onRemoveEditAssetFile,
  onOpenEditModificationRequestDialog,
  onCloseEditModificationRequestDialog,
  onSubmitEditModificationRequestDialog,
  onRequestEditAssetFiles,
  className,
}) => {
  const duration = item.metadata.duration;
  const sender = item.sender;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCancelDialogOpen, setCancelDialog] = useState<boolean>(false);

  useEffect(() => {
    if (videoRef && videoRef.current && duration) {
      const videoEl = videoRef.current;

      const canPlayCallback = () => {
        if (videoEl.paused) {
          videoEl.currentTime = hmsToSeconds(duration);
        }
      };

      // For IE: Required for Initial Rendering.
      videoEl.addEventListener('canplay', () => canPlayCallback(), {
        once: true,
      });

      canPlayCallback();

      return videoEl.removeEventListener('canplay', canPlayCallback);
    }
  }, [duration]);

  return (
    <Root className={className}>
      {deliverable ? (
        <DeliverableScreen>
          <AspectRatioFixed aspectRatio={[4, 3]}>
            <MediaPlayer
              mimeType={deliverable.creative.file.mimeType}
              url={deliverable.creative.file.url!}
              videoRef={videoRef}
            />
          </AspectRatioFixed>
        </DeliverableScreen>
      ) : null}

      <Content>
        <Titles>
          <Title variant="body1">
            <FormattedMessage id="modification_request.creating_card.modification_request_item.title" />
            {(index + 1).toString()}
          </Title>

          {duration &&
          deliverable &&
          (deliverable.creative.file.mimeType.startsWith('video/') ||
            deliverable.creative.file.mimeType.startsWith('audio/')) ? (
            <Duration>{duration}</Duration>
          ) : null}

          <User user={sender} size={24} />
        </Titles>

        <Comment>
          <Typography variant="body1">{item.body}</Typography>
        </Comment>
      </Content>

      <Buttons>
        {deliverable ? (
          <EditModificationRequestButton
            deliverable={deliverable}
            modificationRequestItem={item}
            editModificationRequestItem={editModificationRequestItem}
            files={files}
            bucketId={bucketId}
            index={index}
            onRemoveEditAssetFile={onRemoveEditAssetFile}
            onOpenEditModificationRequestDialog={onOpenEditModificationRequestDialog}
            onCloseEditModificationRequestDialog={onCloseEditModificationRequestDialog}
            onSubmitEditModificationRequestDialog={async (
              index: number,
              editModificationRequestItem: EditModificationRequestItem,
              modificationRequestItemId: string,
              duration: string,
              comment: string,
            ) => {
              setIsSaving(true);
              await onSubmitEditModificationRequestDialog(
                index,
                editModificationRequestItem,
                modificationRequestItemId,
                duration,
                comment,
              );
              setIsSaving(false);
            }}
            onRequestEditAssetFiles={onRequestEditAssetFiles}
            disabled={disabled}
          />
        ) : null}

        {onClickRemoveButton ? (
          <RemoveButton>
            <IconButton onClick={() => setCancelDialog(true)} disabled={disabled}>
              <Delete />
            </IconButton>
          </RemoveButton>
        ) : null}
      </Buttons>

      {isSaving ? (
        <Status>
          <Typography variant="body2" color="primary">
            <FormattedMessage id="message.modification_request_item_saving" />
          </Typography>
        </Status>
      ) : null}

      {onClickRemoveButton ? (
        <InputCancelDialog
          isCancelDialogOpen={isCancelDialogOpen}
          setCancelDialog={setCancelDialog}
          onClickCancelButton={() => {
            onClickRemoveButton(item.id!);
          }}
          descriptionId={'message.modification_request_item.delete_dialog.description'}
          cancelButtonTitleId={'message.modification_request_item.delete_dialog.cancel'}
          submitButtonTitleId={'message.modification_request_item.delete_dialog.submit'}
        />
      ) : null}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['115px', '1fr', 'auto'],
    rows: ['auto'],
    areas: [
      ['deliverable', 'content', 'buttons'],
      ['deliverable', 'content', 'status'],
    ],
  }),
  {
    padding: SPACING_UNIT,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'center' }), {
  marginRight: SPACING_UNIT,
});

const Duration = styled(Typography)({
  marginLeft: SPACING_UNIT,
  textOverflow: 'ellipsis',
});

const Content = styled('div')(gridLayoutItem('content', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
});

const Titles = styled('div')(
  gridLayoutItem('title', { alignSelf: 'flex-start' }),
  flexibleLayoutContainer({ alignItems: 'center' }),
  {
    marginBottom: SPACING_UNIT / 2,
  },
);

const Title = styled(Typography)({ fontWeight: 700, minWidth: 84 });

const User = styled(AvatarAndName)({
  marginLeft: SPACING_UNIT,
});

const Comment = styled('div')({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  width: '100%',
  wordBreak: 'break-word' as any,
  hyphens: 'auto',
});

const Buttons = styled('div')(gridLayoutItem('buttons'), { display: 'flex' });

const EditModificationRequestButton = styled(_EditModificationRequestButton)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const RemoveButton = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const Status = styled('div')(gridLayoutItem('status'), {
  display: 'flex',
  justifyContent: 'center',
});

export default Item;
